import { ReactElement, ReactNode } from 'react'
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import type { ValidationError } from '../../utils'

// Height reserved for the fixed footer so form bodies can pad their bottom and
// avoid having their last fields hidden behind it. Exported so each form can
// apply matching bottom padding.
export const FORM_FOOTER_HEIGHT = 96

export type ValidationSummary = {
	// Heading shown above the listed messages (e.g. "To save a draft:").
	title: string,
	errors: ValidationError[],
}

type Props = {
	// Action buttons (submit, cancel). Rendered right-aligned in the footer bar.
	children: ReactNode,
	// When present, a summary Alert is pinned directly above the footer bar.
	summary?: ValidationSummary | null,
}

// A footer fixed to the viewport bottom that keeps the primary action — and an
// optional validation summary pinned directly above it — in view regardless of
// form length. Shared across every Item Master form for consistent placement.
export default function FormFooter( { children, summary }: Props ): ReactElement {
	const hasErrors = Boolean( summary && summary.errors.length > 0 )

	return (
		<Box
			sx={{
				position: 'fixed',
				bottom: 0,
				left: 0,
				right: 0,
				zIndex: ( theme ) => theme.zIndex.appBar,
			}}
		>
			{hasErrors && (
				<Box sx={{ px: 3, pb: 1 }}>
					<Alert severity="error" variant="filled">
						<AlertTitle>{summary!.title}</AlertTitle>
						<Stack component="ul" spacing={0} sx={{ m: 0, pl: 2 }}>
							{summary!.errors.map( ( err ) => (
								<Typography key={`${err.field}-${err.message}`} component="li" variant="body2">
									{err.message}
								</Typography>
							) )}
						</Stack>
					</Alert>
				</Box>
			)}
			<Paper
				elevation={3}
				square
				sx={{ px: 3, py: 2 }}
			>
				<Stack direction="row" spacing={2} justifyContent="flex-end" alignItems="center">
					{children}
				</Stack>
			</Paper>
		</Box>
	)
}
